/**
 * @file This file contains the classes implementing the interfaces defined in the corresponding types file.
 */

import type { ButtonType } from 'sap/m/library';
import type { URI } from 'sap/ui/core/library';
import { syncRule } from '../../../common/decoration';
import { DEFINITION_LINK_PREFIX, prepareRef } from '../../../common';
import { ControlType, SchemaTag } from '@sap/ux-specification-types';
import type { Definition } from 'typescript-json-schema';
import type { ProcessingRule, ProcessingRulesAdapter, SchemaHandlingParams } from '@sap/ux-specification-types';
import type * as v2 from '@sap/ux-specification-types/src/v2/genericSchemaHandling/controls/Action';
import type { DataFieldForActionAbstractTypes } from '@sap-ux/vocabularies-types/vocabularies/UI';
import { DataFieldTarget } from '../../generate/utils';
import type { DataFieldInfo } from '../../generate/utils';
import { getStableIdForDatafieldActionButton, getSuffixFromIconTabFilterKey } from '../../generate/stableIdUtils';

export class Share implements v2.Share {
    // visible
    @syncRule({
        flex: { controlType: () => ControlType.Button },
        processingRuleAdapter: (processingRule: ProcessingRule): void => {
            processingRule.controlId = 'template::Share';
        }
    })
    visible?: boolean;
}

/**
 * An instance of this type collects the information about an action.
 * Normally the action is based on a DataFieldForAction/DataFieldForIntentBasedNavigation.
 * Instances are created in processingRuleAdapter of property 'actions' of class Toolbar.
 *
 * @property action - the actual action (i.e. the data field) from the ui annotations (if it exists)
 * @property dataField - additional information about the data field which defines this action (if the data field exists)
 * @property isDetermining - true if this action is a determining action (i.e. it is placed in the footer toolbar)
 * @property isChart - true if this action is part of a chart toolbar
 * @property iconTabFilterKey - filled with the key of the view this action belongs to in case the action is part of a table in a multi view multi table setting
 * @property isViewNode - determines whether the action should be visualized as separate node in the page editor tree
 */
export type ActionInfo = {
    action?: DataFieldForActionAbstractTypes;
    dataField?: DataFieldInfo;
    isDetermining?: boolean;
    isChart?: boolean;
    iconTabFilterKey?: string;
    isViewNode: boolean;
};

/**
 * An instance of this type collects the information relevant for handling the properties of an action.
 * Instances of this type are created in the processingRuleAdapter of class Action.
 *
 * @property controlId - the id of the button representing this action
 */
type ActionDetail = {
    controlId?: string; // the stable id of the button representing this action. If not provided the action has no stable id.
};

/**
 * Common syncRule for all properties of an action, since they all have artifactType FlexChange and all belong to the same control.
 */
const syncRuleForAction = {
    flex: {
        controlType: (): ControlType => ControlType.Button
    },
    processingRuleAdapter(processingRule: ProcessingRule, schemaHandlingParams: SchemaHandlingParams): void {
        const actionDetail = schemaHandlingParams.specificParams as ActionDetail;
        if (actionDetail.controlId) {
            processingRule.controlId = actionDetail.controlId;
        } else {
            delete processingRule.element; // properties of an action without stable id cannot be modified by flex changes
        }
    }
};

// Action
// An instance of this class represents one action residing in one toolbar or a column.
// Its processingRuleAdapter function relies on the fact that an instance of ActionInfo
// is provided in schemaHandlingParams.specificParams providing more details on the action.
// Note that all properties of an action have ArtifactType 'FlexChange'. Thereby the control is always the button representing the action.
@syncRule({
    processingRuleAdapter(processingRule: ProcessingRule, schemaHandlingParams: SchemaHandlingParams): void {
        const actionInfo = schemaHandlingParams.specificParams as ActionInfo;
        const element = processingRule.element;
        element[SchemaTag.isViewNode] = actionInfo.isViewNode;
        if (actionInfo.action) {
            element.description = actionInfo.dataField.description;
            element[SchemaTag.keys] = [
                {
                    name: 'Action',
                    value: actionInfo.action.Action
                }
            ];
            if (actionInfo.dataField.message) {
                element[SchemaTag.messages] = [actionInfo.dataField.message];
            }
        } else {
            // todo
        }
        const actionDetail: ActionDetail = {
            controlId: getStableIdForDatafieldActionButton(actionInfo)
        };
        processingRule.referenceAdaptation = {
            specificParams: actionDetail
        };
    }
})
export class Action implements v2.Action {
    // tooltip
    @syncRule(syncRuleForAction)
    tooltip?: string;

    // icon
    @syncRule(syncRuleForAction)
    icon?: URI;

    // activeIcon
    @syncRule(syncRuleForAction)
    activeIcon?: URI;

    // ButtonType
    @syncRule(syncRuleForAction)
    type?: ButtonType;
}

/**
 * An instance of this type collects the information about a list of actions residing in one toolbar.
 * Instances of this type are created in the processingRuleAdapter of property 'actions' of class Toolbar and processed in processingRuleAdapter of class Actions.
 *
 * @property actions - the list of actions in this toolbar
 */
type ActionsInfo = {
    actions: ActionInfo[];
};

/**
 * This function returns the stable id of (the button realizing)an action.
 * Up to now only implemented for annotations based actions.
 *
 * @param actionInfo - information about the action
 * @returns the stable id of the button realizing the action. If no stable id can be generated for the button: undefined.
 */
export function getControlId4ActionInfo(actionInfo: ActionInfo): string {
    if (actionInfo.dataField) {
        return getStableIdForDatafieldActionButton(actionInfo);
    }
    return 'todo'; // todo: handle standard buttons
}

// Actions
// An instance of this class represents a list of actions residing in one toolbar.
// Note that the list of properties to be used for this class is dynamic (depending on the list of actions valid for the current toolbar)
@syncRule({
    processingRuleAdapter(processingRule: ProcessingRule, schemaHandlingParams: SchemaHandlingParams): void {
        const actionsInfo = schemaHandlingParams.specificParams as ActionsInfo;
        const actionInfos = actionsInfo.actions;
        const element = processingRule.element;
        // Build the collection of properties for element based on the list of actions available (i.e. actionsInfo.actions)
        const properties: {
            [key: string]: Definition;
        } = {}; // Initialization
        // In addition to the map property -> Definition which will be used as element.properties we need a ProcessingRulesAdapter
        // that knows how to process each of of these properties.
        // To achieve this we need to make the ActionInfo available for each of these properties. This is the purpose of actionInfoMap:
        const actionInfoMap: {
            [key: string]: ActionInfo;
        } = {};
        actionInfos.forEach(function (actionInfo: ActionInfo, i: number) {
            const propertyDefinition = {
                $ref: DEFINITION_LINK_PREFIX + 'Action'
            } as Definition;
            propertyDefinition[SchemaTag.propertyIndex] = i;
            const property = getControlId4ActionInfo(actionInfo);
            properties[property] = propertyDefinition;
            actionInfoMap[property] = actionInfo;
        });
        element.properties = properties;
        element.additionalProperties = false;
        element[SchemaTag.icon] = 'Export'; // do not use member of enum UiIcons in @sap-ux/ui-components to avoid unwanted dependency
        const processingRulesAdapter: ProcessingRulesAdapter = function (
            property: string,
            processingRule: ProcessingRule
        ): void {
            const actionInfo = actionInfoMap[property];
            // Forward the handling of the property to class Action:
            processingRule.referenceAdaptation = {
                suffix: prepareRef(property), // ensure that a specific copy of type Action will be created in the specific schema
                syncRuleProvider: Action,
                specificParams: actionInfo
            };
        };
        processingRule.referenceAdaptation = {
            processingRulesAdapter
        };
    }
})
export class Actions implements v2.Actions {
    [key: string]: v2.Action;
}

/**
 * An instance of this type collects the information about a toolbar.
 * Instances of this type are created by function adaptProcessingRuleForToolbar (see below) and consumed by the processingRuleAdapter of class Toolbar.
 *
 * @property dataFields - List of data fields appearing in the toolbar
 * @property target - Where is this toolbar used? Possible values are 'Toolbar' (of a table or chart) and 'Footer'
 * @property isChart - is the toolbar shown as toolbar of a chart (only if target is 'Toolbar')
 * @property iconTabFilterKey - in multi view case the key identifying the view. Note that we assume that replaceSpecialCharsInId has already been executed on this value (and therefore must not be called again).
 */
type ToolbarInfo = {
    dataFields: DataFieldInfo[];
    target: DataFieldTarget;
    isChart?: boolean;
    iconTabFilterKey?: string;
};

export class Toolbar implements v2.Toolbar {
    // actions
    @syncRule({
        processingRuleAdapter(processingRule: ProcessingRule, schemaHandlingParams: SchemaHandlingParams): void {
            const toolbarInfo = schemaHandlingParams.specificParams as ToolbarInfo;
            const actionsInfo: ActionsInfo = {
                actions: toolbarInfo.dataFields.map(function (dataFieldInfo: DataFieldInfo): ActionInfo {
                    // All data fields which are placed in a toolbar are instances of DataFieldForActionAbstractTypes which justifies the following type cast
                    const action = dataFieldInfo.dataField as DataFieldForActionAbstractTypes | undefined;
                    return {
                        dataField: dataFieldInfo,
                        action,
                        isDetermining: toolbarInfo.target === DataFieldTarget.Footer,
                        isChart: toolbarInfo.isChart,
                        iconTabFilterKey: toolbarInfo.iconTabFilterKey,
                        isViewNode: true
                    };
                })
            };
            processingRule.referenceAdaptation = {
                suffix: toolbarInfo.target + getSuffixFromIconTabFilterKey(toolbarInfo.iconTabFilterKey),
                syncRuleProvider: Actions,
                specificParams: actionsInfo
            };
        }
    })
    actions: v2.Actions;
}

/**
 * Helper function to be used in the processingRuleAdapter of a node possessing a child node of type Toolbar.
 *
 * @param processingRule - the processingRule to be adapted
 * @param target - location of the toolbar
 * @param dataFieldInfos - List of data fields contributing to the toolbar
 * @param isChart - Is the toolbar shown as a chart toolbar
 * @param iconTabFilterKey - Key identifying the view in multi-view case
 */
export function adaptProcessingRuleForToolbar(
    processingRule: ProcessingRule,
    target: DataFieldTarget,
    dataFieldInfos?: DataFieldInfo[],
    isChart?: boolean,
    iconTabFilterKey?: string
): void {
    if (isChart || !dataFieldInfos) {
        delete processingRule.element; // no toolbar
        return; // todo: How can the toolbar of a chart be determined?
    }
    // Determine the data fields  which contribute to the specified toolbar
    const actionFields = dataFieldInfos.filter(function (datafieldInfo: DataFieldInfo): boolean {
        return datafieldInfo.target === target;
    });
    const toolBarInfo: ToolbarInfo = {
        dataFields: actionFields,
        target,
        isChart,
        iconTabFilterKey
    };
    processingRule.referenceAdaptation = {
        suffix: target + getSuffixFromIconTabFilterKey(toolBarInfo.iconTabFilterKey), // ensure that a specific type is created for this toolbar
        specificParams: toolBarInfo,
        syncRuleProvider: Toolbar
    };
}
